/**
 * StripeTrustBadge — reusable trust block rendered under any card-entry
 * surface (Stripe Elements PaymentElement / CardElement / hosted checkout
 * redirect button).
 *
 * Why this exists: published research on checkout UX reports a 15-20%
 * reduction in payment-form abandonment when a recognized trust badge
 * sits next to the card iframe. Stripe is the most-recognized PCI brand
 * in North America, so we expose Stripe + the underlying protocols
 * (PCI DSS Level 1, TLS 1.3, AES-256, SOC 1/2 Type II, ISO 27001).
 *
 * Three size variants:
 *   - compact:  single line, smallest footprint (inline under one input)
 *   - standard: two-line block w/ wordmark (tenant ad-hoc payments)
 *   - full:     standard block + protocol chip row + Powered by Stripe
 *                image (high-trust surfaces: new rentals, autopay setup)
 *
 * Dark-mode aware via Tailwind's `dark:` variants (driven by ThemeContext
 * applying the `dark` class to <html>). Callers can force a mode with the
 * `darkMode` prop (true | false). Without the prop, Tailwind handles it.
 *
 * Multi-tenant-clean: copy is identical for every facility. No operator-
 * specific defaults, names, or references. Safe to render on tenant-
 * facing public pages with no auth context.
 *
 * Only renders facts Stripe documents publicly on stripe.com/docs/security:
 *   - PCI DSS Level 1 (Stripe is a PCI Service Provider Level 1)
 *   - TLS 1.3 in transit
 *   - AES-256 at rest
 *   - SOC 1 Type II and SOC 2 Type II
 *   - ISO 27001
 *   - Card data tokenized by Stripe Elements; never touches our servers
 */

import React from 'react';
import { ShieldCheck, Lock } from 'lucide-react';

// Stripe's official brand blue.
const STRIPE_BRAND = '#635BFF';

// Locally pinned asset. No render-time external request.
const POWERED_BY_STRIPE_SRC = '/assets/stripe/powered_by_stripe.svg';

// Protocol chips rendered in the `full` variant. Each is a verifiable
// Stripe security control (published at stripe.com/docs/security).
const PROTOCOL_CHIPS = [
  'PCI DSS Level 1',
  'TLS 1.3',
  'AES-256 at rest',
  'SOC 1 Type II',
  'SOC 2 Type II',
  'ISO 27001',
];

const StripeTrustBadge = ({ size = 'compact', darkMode, className = '' }) => {
  // Resolve dark mode: explicit prop wins, otherwise let Tailwind's
  // `dark:` variants do the work via the document's `dark` class.
  const forcedDark = darkMode === true;
  const forcedLight = darkMode === false;

  const wrapperBase = 'mt-3 select-none';
  const wrapperTone = forcedDark
    ? 'text-slate-300'
    : forcedLight
    ? 'text-slate-500'
    : 'text-slate-500 dark:text-slate-300';

  if (size === 'compact') {
    return (
      <div
        className={`${wrapperBase} ${wrapperTone} ${className} flex items-center gap-1.5 text-[11px] leading-tight`}
        data-testid="stripe-trust-badge-compact"
      >
        <Lock
          className="h-3 w-3 flex-shrink-0"
          style={{ color: STRIPE_BRAND }}
          aria-hidden="true"
        />
        <span className="truncate">
          Secured by{' '}
          <span className="font-semibold" style={{ color: STRIPE_BRAND }}>
            Stripe
          </span>
          {' '}&middot; 256-bit TLS &middot; PCI DSS Level 1
        </span>
      </div>
    );
  }

  if (size === 'standard') {
    return (
      <div
        className={`${wrapperBase} ${className}`}
        data-testid="stripe-trust-badge-standard"
      >
        <div
          className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 ${
            forcedDark
              ? 'border-slate-700 bg-slate-800/40'
              : forcedLight
              ? 'border-slate-200 bg-slate-50/60'
              : 'border-slate-200 bg-slate-50/60 dark:border-slate-700 dark:bg-slate-800/40'
          }`}
        >
          <ShieldCheck
            className="h-4 w-4 mt-0.5 flex-shrink-0"
            style={{ color: STRIPE_BRAND }}
            aria-hidden="true"
          />
          <div className="min-w-0">
            <div
              className={`text-[12px] font-semibold ${
                forcedDark
                  ? 'text-slate-100'
                  : forcedLight
                  ? 'text-slate-900'
                  : 'text-slate-900 dark:text-slate-100'
              }`}
            >
              Secured by{' '}
              <span style={{ color: STRIPE_BRAND }}>Stripe</span>
            </div>
            <div className={`mt-0.5 text-[11px] leading-snug ${wrapperTone}`}>
              TLS 1.3 in transit, AES-256 at rest, PCI DSS Level 1 certified.
              SOC 1 &amp; SOC 2 Type II.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // size === 'full'
  return (
    <div
      className={`${wrapperBase} ${className}`}
      data-testid="stripe-trust-badge-full"
    >
      <div
        className={`rounded-lg border px-3.5 py-3 ${
          forcedDark
            ? 'border-slate-700 bg-slate-800/40'
            : forcedLight
            ? 'border-slate-200 bg-slate-50/60'
            : 'border-slate-200 bg-slate-50/60 dark:border-slate-700 dark:bg-slate-800/40'
        }`}
      >
        <div className="flex items-start gap-2.5">
          <ShieldCheck
            className="h-4 w-4 mt-0.5 flex-shrink-0"
            style={{ color: STRIPE_BRAND }}
            aria-hidden="true"
          />
          <div className="min-w-0 flex-1">
            <div
              className={`text-[12px] font-semibold ${
                forcedDark
                  ? 'text-slate-100'
                  : forcedLight
                  ? 'text-slate-900'
                  : 'text-slate-900 dark:text-slate-100'
              }`}
            >
              Secured by{' '}
              <span style={{ color: STRIPE_BRAND }}>Stripe</span>
            </div>
            <div className={`mt-0.5 text-[11px] leading-snug ${wrapperTone}`}>
              TLS 1.3 in transit, AES-256 at rest, PCI DSS Level 1 certified.
              SOC 1 &amp; SOC 2 Type II compliant.
            </div>
          </div>
        </div>

        {/* Protocol chip row */}
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {PROTOCOL_CHIPS.map((chip) => (
            <span
              key={chip}
              className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${
                forcedDark
                  ? 'bg-slate-700/60 text-slate-200'
                  : forcedLight
                  ? 'bg-white text-slate-600 border border-slate-200'
                  : 'bg-white text-slate-600 border border-slate-200 dark:bg-slate-700/60 dark:text-slate-200 dark:border-transparent'
              }`}
            >
              {chip}
            </span>
          ))}
        </div>

        {/* Powered by Stripe asset row */}
        <div className="mt-2.5 flex items-center justify-end">
          <img
            src={POWERED_BY_STRIPE_SRC}
            alt="Powered by Stripe"
            className="h-[22px] w-auto opacity-90"
            loading="lazy"
          />
        </div>
      </div>
    </div>
  );
};

export default StripeTrustBadge;
